import { useEffect, useRef } from "react";

export interface Frame {
  ctx: CanvasRenderingContext2D;
  /** CSS pixels. */
  width: number;
  height: number;
  /** Seconds since the canvas mounted. */
  time: number;
}

interface Options {
  /** Redraw every frame while on screen. When false the canvas is drawn once per resize. */
  animate?: boolean;
  maxDpr?: number;
}

/**
 * Canvas plumbing shared by the procedural scenes: sizes the backing store to
 * the element (device pixel ratio capped), and only runs the frame loop while
 * the canvas is on screen and the tab is visible.
 */
export function useCanvas(draw: (frame: Frame) => void, { animate = true, maxDpr = 2 }: Options = {}) {
  const ref = useRef<HTMLCanvasElement>(null);
  const drawRef = useRef(draw);

  useEffect(() => {
    drawRef.current = draw;
  });

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;

    const started = performance.now();
    let width = 0;
    let height = 0;
    let dpr = 1;
    let raf = 0;
    let onScreen = false;

    const render = (now: number) => {
      if (!width || !height) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawRef.current({ ctx, width, height, time: (now - started) / 1000 });
    };

    const loop = (now: number) => {
      render(now);
      raf = requestAnimationFrame(loop);
    };
    const play = () => {
      if (!raf && animate && onScreen && !document.hidden) raf = requestAnimationFrame(loop);
    };
    const pause = () => {
      cancelAnimationFrame(raf);
      raf = 0;
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, maxDpr);
      width = rect.width;
      height = rect.height;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      render(performance.now());
    };

    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);

    const intersectionObserver = new IntersectionObserver(([entry]) => {
      onScreen = entry.isIntersecting;
      if (onScreen) play();
      else pause();
    });
    intersectionObserver.observe(canvas);

    const onVisibility = () => (document.hidden ? pause() : play());
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      pause();
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [animate, maxDpr]);

  return ref;
}
